import { useEffect, useRef, useState } from "react";
import { onValue, ref } from "firebase/database";
import CreateUser from "./createuser";
import { database } from "./firebase";
import "./dashboard.css";

const getNavItems = (role) => {
  const items = [
    { label: "Overview", value: "overview" },
    { label: "Reports", value: "reports" },
  ];

  if (role === "admin") {
    items.push({ label: "Accounts", value: "accounts" });
  }

  return items;
};

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  "https://agrisense.pro/smart-planting-backend";

const normalizeHistory = (rawHistory) => {
  if (!rawHistory || typeof rawHistory !== "object") {
    return [];
  }

  return Object.entries(rawHistory)
    .map(([key, value]) => {
      if (!value || typeof value !== "object") {
        return null;
      }

      const timestampValue = value.time ?? value.timestamp ?? key;
      const parsedTimestamp = new Date(timestampValue);
      const timestamp = Number.isNaN(parsedTimestamp.getTime())
        ? timestampValue
        : parsedTimestamp.toISOString();
      const soilMoisture = Number(value.soilMoisture ?? value.soil_moisture ?? 0);
      const humidity = Number(value.humidity ?? 0);
      const temperature = Number(value.temperature ?? 0);
      const rawPumpStatus = value.pumpStatus ?? value.pump_status ?? false;
      const pumpStatus =
        rawPumpStatus === true ||
        rawPumpStatus === 1 ||
        rawPumpStatus === "1" ||
        rawPumpStatus === "true" ||
        rawPumpStatus === "on";

      return {
        firebaseKey: key,
        time: timestamp,
        soilMoisture,
        humidity,
        temperature,
        pumpStatus,
      };
    })
    .filter(Boolean)
    .sort((a, b) => new Date(a.time) - new Date(b.time))
    .slice(-12);
};

const normalizeStoredHistory = (readings) =>
  (Array.isArray(readings) ? readings : []).map((entry) => ({
    firebaseKey: entry.firebaseKey,
    time: entry.measuredAt,
    soilMoisture: Number(entry.soilMoisture),
    humidity: Number(entry.humidity),
    temperature: Number(entry.temperature),
    pumpStatus: Boolean(entry.pumpStatus),
  }));

const fallbackZone = {
  name: "Zone A",
  soilMoisture: 40,
  humidity: 65,
  temperature: 28.5,
  moistureThreshold: 40,
  pumpStatus: false,
  autoWatering: true,
};

const ONION_PLANTING_DATE_KEY = "smart_planting_zone_A_onion_planting_date";

function OnionPrediction({ zone }) {
  const [plantingDate, setPlantingDate] = useState(
    () => localStorage.getItem(ONION_PLANTING_DATE_KEY) || ""
  );
  const today = new Date();
  const todayUtc = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
  const plantingUtc = plantingDate
    ? Date.parse(`${plantingDate}T00:00:00Z`)
    : Number.NaN;
  const daysSincePlanting = Number.isNaN(plantingUtc)
    ? null
    : Math.floor((todayUtc - plantingUtc) / (24 * 60 * 60 * 1000));
  const earliestHarvest = Number.isNaN(plantingUtc)
    ? null
    : new Date(plantingUtc + 80 * 24 * 60 * 60 * 1000);
  const latestHarvest = Number.isNaN(plantingUtc)
    ? null
    : new Date(plantingUtc + 120 * 24 * 60 * 60 * 1000);
  const formatDate = (date) =>
    date.toLocaleDateString(undefined, { timeZone: "UTC", dateStyle: "medium" });

  let predictionMessage = "Enter the planting date to calculate the estimated harvest window.";
  if (daysSincePlanting !== null) {
    if (daysSincePlanting < 0) {
      predictionMessage = `Planting date is in ${Math.abs(daysSincePlanting)} days.`;
    } else if (daysSincePlanting < 80) {
      predictionMessage = `Estimated harvest window begins in ${80 - daysSincePlanting} days.`;
    } else if (daysSincePlanting <= 120) {
      predictionMessage = `Within the expected harvest window (day ${daysSincePlanting} after planting).`;
    } else {
      predictionMessage = `${daysSincePlanting - 120} days past the expected maturity window. Check the bulbs and tops for harvest readiness.`;
    }
  }

  const handlePlantingDateChange = (event) => {
    const nextDate = event.target.value;
    setPlantingDate(nextDate);
    if (nextDate) {
      localStorage.setItem(ONION_PLANTING_DATE_KEY, nextDate);
    } else {
      localStorage.removeItem(ONION_PLANTING_DATE_KEY);
    }
  };

  return (
    <section className="panel onion-prediction" aria-labelledby="onion-prediction-heading">
      <div className="onion-prediction-header">
        <div>
          <p className="eyebrow">Crop forecast · {zone?.name || "Zone A"}</p>
          <h3 id="onion-prediction-heading">Onion harvest estimate</h3>
        </div>
        <span className="status-pill good">80–120 days</span>
      </div>

      <label className="planting-date-field">
        Onion planting date
        <input
          type="date"
          value={plantingDate}
          onChange={handlePlantingDateChange}
          aria-label="Onion planting date"
        />
      </label>

      {earliestHarvest && latestHarvest ? (
        <div className="harvest-window" aria-live="polite">
          <div>
            <span>Estimated window starts</span>
            <strong>{formatDate(earliestHarvest)}</strong>
          </div>
          <div>
            <span>Latest expected maturity</span>
            <strong>{formatDate(latestHarvest)}</strong>
          </div>
          <p>{predictionMessage}</p>
        </div>
      ) : (
        <p className="metric-note">{predictionMessage}</p>
      )}

      <div className="onion-water-guidance">
        <strong>Watering guideline</strong>
        <p>
          Aim for about 1 inch (2.5 cm) of water per week from rain and irrigation. Keep soil
          consistently moist, especially while bulbs are swelling, and adjust for hot, dry, or windy
          weather.
        </p>
        <span>
          Current soil moisture sensor reading: {zone?.soilMoisture ?? 0}%. This percentage does not
          measure weekly water volume.
        </span>
      </div>
      <p className="metric-note">
        This is a calendar-based estimate from the planting date, not a maturity diagnosis. Confirm
        readiness by inspecting the onions.
      </p>
    </section>
  );
}

function ReportsPanel({ history, zone, syncStatus, syncError }) {
  const onionPrediction = <OnionPrediction zone={zone} />;

  if (!history || history.length === 0) {
    return (
      <section className="reports-panel">
        <div className="hero-panel">
          <div>
            <p className="eyebrow">Historical report</p>
            <h2>Soil analytics summary</h2>
          </div>
          <span className={`status-pill ${syncStatus === "error" ? "warning" : "good"}`}>
            {syncStatus === "error" ? "Database sync issue" : syncStatus}
          </span>
        </div>

        {onionPrediction}

        <div className="panel empty-panel">
          <h3>No historical readings yet</h3>
          <p>
            Readings from <strong>sensorHistory/zone_A</strong> will be copied from Firebase into
            MariaDB and appear here.
          </p>
          {syncError ? <p className="sync-error" role="alert">{syncError}</p> : null}
        </div>
      </section>
    );
  }

  const metrics = history.reduce(
    (accumulator, entry) => {
      accumulator.soilMoisture.push(Number(entry.soilMoisture || 0));
      accumulator.humidity.push(Number(entry.humidity || 0));
      accumulator.temperature.push(Number(entry.temperature || 0));
      accumulator.pumpRuns += entry.pumpStatus ? 1 : 0;
      return accumulator;
    },
    { soilMoisture: [], humidity: [], temperature: [], pumpRuns: 0 }
  );

  const avgMoisture =
    metrics.soilMoisture.reduce((sum, value) => sum + value, 0) / (metrics.soilMoisture.length || 1);
  const avgHumidity =
    metrics.humidity.reduce((sum, value) => sum + value, 0) / (metrics.humidity.length || 1);
  const avgTemperature =
    metrics.temperature.reduce((sum, value) => sum + value, 0) / (metrics.temperature.length || 1);

  const alertItems = [
    {
      label: "Low moisture risk",
      active: avgMoisture < 40,
      detail: `${Math.round(avgMoisture)}% average moisture`,
    },
    {
      label: "Humidity condition",
      active: avgHumidity < 55,
      detail: `${Math.round(avgHumidity)}% average humidity`,
    },
    {
      label: "Temperature spike",
      active: avgTemperature > 30,
      detail: `${avgTemperature.toFixed(1)}°C average`,
    },
    {
      label: "Pump activity",
      active: metrics.pumpRuns > 2,
      detail: `${metrics.pumpRuns} watering events logged`,
    },
  ];

  const maxMoisture = Math.max(...metrics.soilMoisture, 100);

  return (
    <section className="reports-panel">
      <div className="hero-panel">
        <div>
          <p className="eyebrow">Historical report</p>
          <h2>Soil analytics summary</h2>
        </div>
        <span className={`status-pill ${syncStatus === "error" ? "warning" : "good"}`}>
          {syncStatus}
        </span>
      </div>
      {syncError ? <p className="sync-error" role="alert">{syncError}</p> : null}

      {onionPrediction}

      <div className="metrics-grid">
        <article className="metric-card accent-green">
          <span className="metric-label">Avg soil moisture</span>
          <strong className="metric-value">{avgMoisture.toFixed(1)}%</strong>
          <span className="metric-note">Trend across recent checks</span>
        </article>

        <article className="metric-card accent-blue">
          <span className="metric-label">Avg humidity</span>
          <strong className="metric-value">{avgHumidity.toFixed(1)}%</strong>
          <span className="metric-note">Air moisture and retention</span>
        </article>

        <article className="metric-card accent-cyan">
          <span className="metric-label">Avg temperature</span>
          <strong className="metric-value">{avgTemperature.toFixed(1)}°C</strong>
          <span className="metric-note">Environmental balance</span>
        </article>

        <article className="metric-card accent-gold">
          <span className="metric-label">Pump runs</span>
          <strong className="metric-value">{metrics.pumpRuns}</strong>
          <span className="metric-note">Recent watering cycles</span>
        </article>
      </div>

      <div className="detail-grid reports-grid">
        <div className="panel">
          <h3>Trend overview</h3>
          <div className="trend-list" aria-label="Moisture and climate trend overview">
            {history.map((entry, index) => (
              <div key={entry.time} className="trend-row">
                <div className="trend-labels">
                  <span>{new Date(entry.time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                  <strong>{entry.soilMoisture}%</strong>
                </div>
                <div className="mini-bars" aria-hidden="true">
                  <span className="bar moisture" style={{ height: `${(entry.soilMoisture / maxMoisture) * 100}%` }} />
                  <span className="bar humidity" style={{ height: `${entry.humidity}%` }} />
                  <span className="bar temperature" style={{ height: `${(entry.temperature / 36) * 100}%` }} />
                </div>
                <span className={`pump-tag ${entry.pumpStatus ? "on" : "off"}`}>
                  {entry.pumpStatus ? "Pump ON" : "Pump OFF"}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="panel">
          <h3>System alerts</h3>
          <ul className="alert-list">
            {alertItems.map((alert) => (
              <li key={alert.label} className={alert.active ? "alert-item warning" : "alert-item safe"}>
                <div>
                  <strong>{alert.label}</strong>
                  <span>{alert.detail}</span>
                </div>
                <span className={alert.active ? "alert-dot warning" : "alert-dot good"} />
              </li>
            ))}
          </ul>

          <div className="summary-box">
            <h4>Zone snapshot</h4>
            <ul className="status-list compact">
              <li>
                <span>Current moisture</span>
                <strong>{zone?.soilMoisture ?? 0}%</strong>
              </li>
              <li>
                <span>Current humidity</span>
                <strong>{zone?.humidity ?? 0}%</strong>
              </li>
              <li>
                <span>Current temperature</span>
                <strong>{zone?.temperature ?? 0}°C</strong>
              </li>
              <li>
                <span>Pump status</span>
                <strong>{zone?.pumpStatus ? "ON" : "OFF"}</strong>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

function Dashboard({ user, onLogout }) {
  const [activeView, setActiveView] = useState("overview");
  const [zone, setZone] = useState(fallbackZone);
  const [history, setHistory] = useState([]);
  const [historySyncStatus, setHistorySyncStatus] = useState("Connecting to database");
  const [historySyncError, setHistorySyncError] = useState("");
  const [error, setError] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const previousZoneRef = useRef(null);
  const isMountedRef = useRef(true);
  const syncedReadingsRef = useRef(new Map());
  const lastLiveReadingRef = useRef("");
  const liveReadingSequenceRef = useRef(0);
  const navItems = getNavItems(user?.role || "user");

  const pushNotification = (title, message, level = "info") => {
    setNotifications((current) => [{ id: Date.now() + Math.random(), title, message, level }, ...current].slice(0, 4));

    if (typeof window !== "undefined" && "Notification" in window && Notification.permission === "granted") {
      new Notification(title, { body: message, icon: "/favicon.ico" });
    }
  };

  const requestAlertPermission = async () => {
    if (typeof window === "undefined" || !("Notification" in window)) {
      pushNotification("Browser alerts unavailable", "This browser does not support desktop notifications.", "warning");
      return;
    }

    const permission = await Notification.requestPermission();

    if (permission === "granted") {
      pushNotification("Alerts enabled", "You will now receive notifications for status changes.", "success");
      return;
    }

    pushNotification("Alerts disabled", "Notifications are off, but in-app alerts are still active.", "warning");
  };

  useEffect(() => {
    if (user?.role !== "admin" && activeView === "accounts") {
      setActiveView("overview");
    }
  }, [user?.role, activeView]);

  useEffect(() => {
    if (!previousZoneRef.current) {
      previousZoneRef.current = zone;
      return;
    }

    const previous = previousZoneRef.current;
    const messages = [];

    if (previous.soilMoisture !== zone.soilMoisture) {
      const direction = zone.soilMoisture < previous.soilMoisture ? "dropped" : "rose";
      messages.push({
        title: "Soil moisture changed",
        message: `Soil moisture ${direction} to ${zone.soilMoisture}%`,
        level: zone.soilMoisture < zone.moistureThreshold * 0.8 ? "warning" : "info",
      });
    }

    if (previous.humidity !== zone.humidity) {
      messages.push({
        title: "Humidity changed",
        message: `Humidity is now ${zone.humidity}%`,
        level: zone.humidity < 55 ? "warning" : "info",
      });
    }

    if (previous.temperature !== zone.temperature) {
      messages.push({
        title: "Temperature changed",
        message: `Temperature is now ${zone.temperature}°C`,
        level: zone.temperature > 30 ? "warning" : "info",
      });
    }

    if (previous.pumpStatus !== zone.pumpStatus) {
      messages.push({
        title: "Pump status changed",
        message: `Pump is now ${zone.pumpStatus ? "running" : "idle"}`,
        level: zone.pumpStatus ? "success" : "info",
      });
    }

    if (messages.length > 0) {
      messages.forEach((message) => {
        pushNotification(message.title, message.message, message.level);
      });
    }

    previousZoneRef.current = zone;
  }, [zone]);

  useEffect(() => {
    isMountedRef.current = true;

    if (!database || typeof ref !== "function" || typeof onValue !== "function") {
      setZone(fallbackZone);
      setHistory([]);
      setHistorySyncStatus("Firebase unavailable");
      setHistorySyncError("Firebase is not configured; readings cannot be synchronized.");
      return;
    }

    const zoneRef = ref(database, "zones/zone_A");
    const historyRef = ref(database, "sensorHistory/zone_A");

    const saveLiveReading = async (rawZone) => {
      const soilMoisture = Number(rawZone.soilMoisture ?? rawZone.soil_moisture);
      const humidity = Number(rawZone.humidity);
      const temperature = Number(rawZone.temperature);

      if (
        !Number.isFinite(soilMoisture) ||
        !Number.isFinite(humidity) ||
        !Number.isFinite(temperature)
      ) {
        setHistorySyncStatus("Invalid live data");
        setHistorySyncError("Firebase zone data is missing valid moisture, humidity, or temperature values.");
        return;
      }

      const rawPumpStatus = rawZone.pumpStatus ?? rawZone.pump_status ?? false;
      const pumpStatus =
        rawPumpStatus === true ||
        rawPumpStatus === 1 ||
        rawPumpStatus === "1" ||
        rawPumpStatus === "true" ||
        rawPumpStatus === "on";
      const fingerprint = JSON.stringify({
        soilMoisture,
        humidity,
        temperature,
        pumpStatus,
      });

      if (fingerprint === lastLiveReadingRef.current) {
        return;
      }

      lastLiveReadingRef.current = fingerprint;
      liveReadingSequenceRef.current += 1;
      const time = new Date().toISOString();
      const reading = {
        firebaseKey: `zone_A_live_${Date.now()}_${liveReadingSequenceRef.current}`,
        time,
        soilMoisture,
        humidity,
        temperature,
        pumpStatus,
      };

      setHistorySyncStatus("Sending live reading to MariaDB");
      setHistorySyncError("");

      try {
        const response = await fetch(`${API_BASE_URL}/sensor-readings.php`, {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({ zone: "zone_A", readings: [reading] }),
        });
        const data = await response.json().catch(() => ({}));

        if (!response.ok || data.success !== true) {
          throw new Error(data.message || "Could not save the live reading to MariaDB.");
        }

        if (isMountedRef.current) {
          setHistorySyncStatus("Live reading saved to MariaDB");
          await loadStoredHistory();
        }
      } catch (syncError) {
        if (lastLiveReadingRef.current === fingerprint) {
          lastLiveReadingRef.current = "";
        }

        console.error("MariaDB live sensor sync error:", syncError);
        if (isMountedRef.current) {
          setHistorySyncStatus("Database unavailable");
          setHistorySyncError(syncError.message || "Could not save the live reading to MariaDB.");
        }
      }
    };

    const loadStoredHistory = async () => {
      const response = await fetch(
        `${API_BASE_URL}/sensor-readings.php?zone=zone_A&limit=12`,
        { credentials: "include", headers: { Accept: "application/json" } }
      );
      const data = await response.json().catch(() => ({}));

      if (!response.ok || data.success !== true || !Array.isArray(data.readings)) {
        throw new Error(data.message || "Could not load stored sensor readings.");
      }

      if (isMountedRef.current) {
        setHistory(normalizeStoredHistory(data.readings));
        setHistorySyncStatus("MariaDB synced");
        setHistorySyncError("");
      }
    };

    const zoneUnsubscribe = onValue(
      zoneRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const rawZone = snapshot.val();
          setZone({ ...fallbackZone, ...rawZone });
          setError(null);
          saveLiveReading(rawZone);
        } else {
          setZone(fallbackZone);
          setError(null);
        }
      },
      (firebaseError) => {
        console.error("Firebase zone error:", firebaseError);
        setZone(fallbackZone);
        setError(null);
      }
    );

    const historyUnsubscribe = onValue(
      historyRef,
      (snapshot) => {
        const normalized = normalizeHistory(snapshot.val()).slice(-500);

        setHistory(normalized.slice(-12));
        setHistorySyncStatus("Syncing to MariaDB");
        setHistorySyncError("");

        if (normalized.length === 0) {
          loadStoredHistory().catch((syncError) => {
            console.error("MariaDB history load error:", syncError);
            if (isMountedRef.current) {
              setHistorySyncStatus("Database unavailable");
              setHistorySyncError(syncError.message || "Could not load MariaDB history.");
            }
          });
          return;
        }

        const pendingReadings = normalized.filter((entry) => {
          const key = String(entry.firebaseKey);
          const fingerprint = JSON.stringify(entry);
          return syncedReadingsRef.current.get(key) !== fingerprint;
        });

        if (pendingReadings.length === 0) {
          setHistorySyncStatus("MariaDB synced");
          return;
        }

        fetch(`${API_BASE_URL}/sensor-readings.php`, {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({ zone: "zone_A", readings: pendingReadings }),
        })
          .then(async (response) => {
            const data = await response.json().catch(() => ({}));
            if (!response.ok || data.success !== true) {
              throw new Error(data.message || "Could not save readings to MariaDB.");
            }

            pendingReadings.forEach((entry) => {
              syncedReadingsRef.current.set(
                String(entry.firebaseKey),
                JSON.stringify(entry)
              );
            });
            await loadStoredHistory();
          })
          .catch((syncError) => {
            console.error("MariaDB sensor sync error:", syncError);
            if (isMountedRef.current) {
              setHistorySyncStatus("Database unavailable");
              setHistorySyncError(syncError.message || "Could not save readings to MariaDB.");
            }
          });
      },
      (firebaseError) => {
        console.error("Firebase history error:", firebaseError);
        setHistory([]);
        setHistorySyncStatus("Firebase unavailable");
        setHistorySyncError("Could not read sensor history from Firebase.");
      }
    );

    return () => {
      isMountedRef.current = false;
      if (typeof zoneUnsubscribe === "function") {
        zoneUnsubscribe();
      }
      if (typeof historyUnsubscribe === "function") {
        historyUnsubscribe();
      }
    };
  }, []);

  const getSystemStatus = () => {
    if (!zone) return "Checking sensors";
    if (zone.soilMoisture < zone.moistureThreshold * 0.8) return "Low moisture";
    if (zone.soilMoisture > zone.moistureThreshold * 1.4) return "Overwatered";
    return "Operating normally";
  };

  const systemStatus = getSystemStatus();
  const humidity = zone?.humidity ?? 0;

  if (error) {
    return (
      <div className="app-shell error-state">
        <div className="message-card">
          <p className="eyebrow">System alert</p>
          <h1>Firebase Error</h1>
          <p>{error}</p>
          <button className="secondary-button" type="button" onClick={onLogout}>
            Log out
          </button>
        </div>
      </div>
    );
  }

  if (!zone) {
    return (
      <div className="app-shell loading-state">
        <div className="message-card loading-card">
          <div className="spinner" aria-label="Loading" />
          <p className="eyebrow">Monitoring</p>
          <h1>Loading system data...</h1>
        </div>
      </div>
    );
  }

  return (
    <div className="app-shell dashboard-layout">
      <aside className="sidebar">
        <div className="sidebar-brand">
          <div className="brand-mark">AGRISENSE</div>
          <div>
            <p className="eyebrow">AGRISENSE</p>
            <h2>Control Center</h2>
          </div>
        </div>

        <nav className="sidebar-nav" aria-label="Sidebar navigation">
          {navItems.map((item) => (
            <button
              key={item.value}
              type="button"
              className={`nav-item ${activeView === item.value ? "active" : ""}`}
              onClick={() => setActiveView(item.value)}
            >
              {item.label}
            </button>
          ))}
        </nav>

        <div className="sidebar-card">
          <p className="eyebrow">Current user</p>
          <strong>{user?.name || user?.username || "User"}</strong>
          <span>{(user?.role || "user").toUpperCase()}</span>
        </div>
      </aside>

      <div className="dashboard-main">
        <header className="topbar">
          <div className="brand-wrap">
            <div>
              <p className="eyebrow">AGRISENSE irrigation</p>
              <h1>AGRISENSE Dashboard</h1>
            </div>
          </div>

          <div className="topbar-actions">
            <div className="user-tag">
              <span>Signed in as</span>
              <strong>{user?.name || user?.username || "User"}</strong>
            </div>

            <div className="notification-wrapper">
              <button
                type="button"
                className="notification-bell"
                onClick={() => setNotificationOpen((current) => !current)}
                aria-label="Toggle notifications"
              >
                <span aria-hidden="true">🔔</span>
                {notifications.length > 0 ? <span className="notification-badge">{notifications.length}</span> : null}
              </button>

              {notificationOpen && notifications.length > 0 ? (
                <div className="notification-panel" aria-live="polite">
                  {notifications.map((notice) => (
                    <div key={notice.id} className={`notification-item ${notice.level}`}>
                      <div className="notification-copy">
                        <strong>{notice.title}</strong>
                        <span>{notice.message}</span>
                      </div>
                      <button
                        type="button"
                        className="notification-close"
                        onClick={() => setNotifications((current) => current.filter((item) => item.id !== notice.id))}
                        aria-label="Dismiss notification"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>

            <button className="secondary-button alert-toggle" type="button" onClick={requestAlertPermission}>
              Enable alerts
            </button>
            <button className="primary-action" type="button" onClick={onLogout}>
              Log out
            </button>
          </div>
        </header>

        <main className="dashboard">
          {activeView === "overview" ? (
            <>
              <section className="hero-panel">
                <div>
                  <p className="eyebrow">Zone overview</p>
                  <h2>{zone.name}</h2>
                </div>
                <span className={`status-pill ${systemStatus === "Operating normally" ? "good" : "warning"}`}>
                  {systemStatus}
                </span>
              </section>

              <section className="metrics-grid">
                <article className="metric-card accent-green">
                  <span className="metric-label">Soil Moisture</span>
                  <strong className="metric-value">{zone.soilMoisture}%</strong>
                  <span className="metric-note">Target: {zone.moistureThreshold}%</span>
                </article>

                <article className="metric-card accent-blue">
                  <span className="metric-label">Humidity</span>
                  <strong className="metric-value">{humidity}%</strong>
                  <span className="metric-note">Air moisture level</span>
                </article>

                <article className="metric-card accent-cyan">
                  <span className="metric-label">Temperature</span>
                  <strong className="metric-value">{zone.temperature}°C</strong>
                  <span className="metric-note">Optimal for growth</span>
                </article>

                <article className="metric-card accent-gold">
                  <span className="metric-label">Pump Status</span>
                  <strong className="metric-value">{zone.pumpStatus ? "Running" : "Idle"}</strong>
                  <span className="metric-note">{zone.pumpStatus ? "Watering active" : "Waiting for cycle"}</span>
                </article>
              </section>

              <section className="detail-grid">
                <div className="panel">
                  <h3>System status</h3>
                  <ul className="status-list">
                    <li>
                      <span>Pump status</span>
                      <strong>{zone.pumpStatus ? "ON" : "OFF"}</strong>
                    </li>
                    <li>
                      <span>Humidity</span>
                      <strong>{humidity}%</strong>
                    </li>
                    <li>
                      <span>Automatic watering</span>
                      <strong>{zone.autoWatering ? "Enabled" : "Disabled"}</strong>
                    </li>
                    <li>
                      <span>Moisture threshold</span>
                      <strong>{zone.moistureThreshold}%</strong>
                    </li>
                  </ul>
                </div>

                <div className="panel">
                  <h3>Automation</h3>
                  <div className="automation-row">
                    <div>
                      <p>Watering mode</p>
                      <strong>{zone.autoWatering ? "Auto schedule" : "Manual control"}</strong>
                    </div>
                    <span className={`toggle ${zone.autoWatering ? "on" : "off"}`}>
                      {zone.autoWatering ? "ON" : "OFF"}
                    </span>
                  </div>
                  <div className="progress-block">
                    <div className="progress-header">
                      <span>Moisture level</span>
                      <strong>{zone.soilMoisture}%</strong>
                    </div>
                    <div className="progress-bar">
                      <span style={{ width: `${Math.min(zone.soilMoisture, 100)}%` }} />
                    </div>
                  </div>
                </div>
              </section>
            </>
          ) : null}

          {activeView === "reports" ? (
            <ReportsPanel
              history={history}
              zone={zone}
              syncStatus={historySyncStatus}
              syncError={historySyncError}
            />
          ) : null}

          {activeView === "accounts" && user?.role === "admin" ? (
            <section className="admin-create-section">
              <CreateUser apiBaseUrl={API_BASE_URL} />
            </section>
          ) : null}

          {activeView === "accounts" && user?.role !== "admin" ? (
            <section className="panel access-denied-panel">
              <h3>Access denied</h3>
              <p>Only administrators can manage accounts.</p>
            </section>
          ) : null}
        </main>
      </div>
    </div>
  );
}

export default Dashboard;
